import type { BookingAction, BookingState } from '../state/machine';

/** Which booking field the user is editing from the confirm screen. */
export type BookingEditField = 'lead' | 'location' | 'fittingType' | 'dateTime';

/** Individual strip fields surfaced in the mobile selection summary header. */
export type StripBookingField = 'location' | 'fittingType' | 'date' | 'fitter' | 'time';

/** Progressive enablement for the revealed booking panel (ADR: location-first strip). */
export function getBookingPanelEnablement(state: BookingState) {
  const hasLocation = !!state.selectedLocation;
  const hasFittingType = !!state.selectedFittingType;
  const hasDate = !!state.selectedDate;

  return {
    showPanel: hasLocation,
    fittingTypeEnabled: hasLocation,
    dateAndFitterEnabled: hasLocation && hasFittingType,
    timeEnabled: hasLocation && hasFittingType && hasDate,
    bookEnabled: !!state.selectedSlot,
  };
}

/** Reset the field (and dependents per reducer rules) for inline "Change" from confirm. */
export function bookingEditFieldAction(field: BookingEditField): BookingAction {
  switch (field) {
    case 'lead':
      // Lead capture (name + email) is preserved when bouncing back —
      // the user is editing in place, not starting over.
      return { type: 'CLEAR_SUBMIT_ERROR' };
    case 'location':
      return { type: 'SELECT_LOCATION', payload: null };
    case 'fittingType':
      return { type: 'SELECT_FITTING_TYPE', payload: null };
    case 'dateTime':
      return { type: 'SELECT_DATE', payload: null };
  }
}

/** Reset actions for mobile strip "Change" — may include companion clears (e.g. location query). */
export function stripBookingFieldActions(field: StripBookingField): BookingAction[] {
  switch (field) {
    case 'location':
      return [
        { type: 'SELECT_LOCATION', payload: null },
        { type: 'SET_LOCATION_QUERY', payload: '' },
      ];
    case 'fittingType':
      return [{ type: 'SELECT_FITTING_TYPE', payload: null }];
    case 'date':
      return [{ type: 'SELECT_DATE', payload: null }];
    case 'fitter':
      return [{ type: 'SELECT_FITTER', payload: null }];
    case 'time':
      return [{ type: 'SELECT_TIME', payload: null }];
  }
}



/** Dispatched when leaving confirm via Change.
 *
 * Intentionally does NOT clear the field. Clearing the field also cascades
 * through the reducer and wipes dependents (time/slot/fitter), forcing the
 * user to re-pick everything to land back at this same state. We just want
 * them to be able to change ONE thing if they choose — usually they don't.
 *
 * Behavior on return to the booking strip:
 *   - All previously-selected values remain visible and selected
 *   - The user can change any field; the normal reducer cascade fires on
 *     their new selection (not on the act of clicking "Change")
 *   - If they don't change anything, clicking Book Fitting / Confirm goes
 *     straight through with the same slot
 */
export function bookingEditFromConfirmActions(_field: BookingEditField): BookingAction[] {
  return [{ type: 'CLEAR_SUBMIT_ERROR' }];
}
